import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Calculator, Palette, PawPrint, ShieldAlert, Car, ArrowLeft, Volume2, BookText, TreePine, Sun, Apple } from 'lucide-react';
import { useGame } from '../context/GameContext';

const PreschoolTopics = () => {
  const navigate = useNavigate();
  const { playerName, setSelectedSubject } = useGame();
  const [hoveredTopic, setHoveredTopic] = useState(null);
  const [playingSound, setPlayingSound] = useState(null);

  // Cancel any speech when component unmounts
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const topics = [
    {
      id: 'abc-adventures',
      label: 'ABC Adventures',
      icon: BookOpen,
      color: 'from-purple-400 to-pink-400',
      bgColor: '#E879F9',
      image: 'https://images.unsplash.com/photo-1539632346654-dd4c3cffad8c?w=400&h=300&fit=crop',
      emoji: '📚',
      description: 'Learn letters and sounds!',
      sound: 'A, B, C! Let\'s learn the alphabet!',
      funFact: 'Did you know? A is for Apple! 🍎'
    },
    {
      id: 'counting-fun',
      label: 'Counting Fun',
      icon: Calculator,
      color: 'from-blue-400 to-cyan-400',
      bgColor: '#38BDF8',
      image: 'https://images.pexels.com/photos/7111523/pexels-photo-7111523.jpeg?w=400&h=300&fit=crop',
      emoji: '🔢',
      description: 'Count and play with numbers!',
      sound: '1, 2, 3! Can you count with me?',
      funFact: 'Count your fingers! You have 10! 🖐️🖐️'
    },
    {
      id: 'colors-shapes',
      label: 'Colors & Shapes',
      icon: Palette,
      color: 'from-red-400 to-orange-400',
      bgColor: '#FB923C',
      image: 'https://images.unsplash.com/photo-1595707678349-4b3f482bfbd3?w=400&h=300&fit=crop',
      emoji: '🎨',
      description: 'Explore colors and shapes!',
      sound: 'Red, blue, yellow! Circle, square, triangle!',
      funFact: 'The sun is a circle! ☀️'
    },
    {
      id: 'animal-friends',
      label: 'Animal Friends',
      icon: PawPrint,
      color: 'from-green-400 to-emerald-400',
      bgColor: '#4ADE80',
      image: 'https://images.pexels.com/photos/5435600/pexels-photo-5435600.jpeg?w=400&h=300&fit=crop',
      emoji: '🐶',
      description: 'Meet cute animals!',
      sound: 'Woof woof! Meow meow! What sound does a cow make?',
      funFact: 'Dogs can hear 4 times better than humans! 🐕'
    },
    {
      id: 'safety-first',
      label: 'Safety First',
      icon: ShieldAlert,
      color: 'from-yellow-400 to-amber-400',
      bgColor: '#FBBF24',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop',
      emoji: '🛡️',
      description: 'Learn to stay safe!',
      sound: 'Stop, look, and listen! Stay safe and be careful!',
      funFact: 'Always hold a grown-up\'s hand when crossing! 🚸'
    },
    {
      id: 'transportation',
      label: 'Transportation',
      icon: Car,
      color: 'from-indigo-400 to-purple-400',
      bgColor: '#A78BFA',
      image: 'https://images.unsplash.com/photo-1592784082277-46e33f897110?w=400&h=300&fit=crop',
      emoji: '🚗',
      description: 'Cars, trains, and planes!',
      sound: 'Vroom vroom! Choo choo! Zoom! Let\'s go!',
      funFact: 'Trains can be as long as 2 miles! 🚂'
    },
    {
      id: 'reading',
      label: 'Reading Time',
      icon: BookText,
      color: 'from-rose-400 to-pink-500',
      bgColor: '#FB7185',
      image: 'https://images.unsplash.com/photo-1549737221-bef65e2604a6?w=400&h=300&fit=crop',
      emoji: '📖',
      description: 'Stories and adventures!',
      sound: 'Once upon a time! Let\'s read a story together!',
      funFact: 'Reading takes you to magical places! ✨'
    },
    {
      id: 'nature-outdoors',
      label: 'Nature & Outdoors',
      icon: TreePine,
      color: 'from-lime-400 to-green-500',
      bgColor: '#84CC16',
      image: 'https://images.unsplash.com/photo-1690843857685-15c9a042d37b?w=400&h=300&fit=crop',
      emoji: '🌳',
      description: 'Explore the wonderful world!',
      sound: 'Trees, flowers, and butterflies! Nature is beautiful!',
      funFact: 'Trees give us oxygen to breathe! 🌲'
    },
    {
      id: 'daily-activities',
      label: 'Daily Activities',
      icon: Sun,
      color: 'from-sky-400 to-blue-500',
      bgColor: '#38BDF8',
      image: 'https://images.unsplash.com/photo-1597075958693-75173d1c837f?w=400&h=300&fit=crop',
      emoji: '🌅',
      description: 'Learn about your daily routine!',
      sound: 'Wake up, brush your teeth, eat breakfast! What comes next?',
      funFact: 'Brushing teeth keeps your smile bright! 😁'
    },
    {
      id: 'food',
      label: 'Yummy Food',
      icon: Apple,
      color: 'from-red-400 to-rose-500',
      bgColor: '#F87171',
      image: 'https://images.unsplash.com/photo-1647830168692-f85690b072bf?w=400&h=300&fit=crop',
      emoji: '🍎',
      description: 'Healthy and delicious foods!',
      sound: 'Apples, bananas, carrots! Yummy healthy food!',
      funFact: 'Carrots help you see better at night! 🥕'
    }
  ];

  const playTopicSound = (text, rate = 0.85, pitch = 1.2) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = 0.8;
    window.speechSynthesis.speak(utterance);
  };

  const handleTopicClick = (topic) => {
    setPlayingSound(topic.id);
    playTopicSound(topic.sound);
    
    setTimeout(() => {
      setSelectedSubject(topic.id);
      navigate('/game-format');
    }, 1500);
  };

  const handleTopicHover = (topic) => {
    setHoveredTopic(topic.id);
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
    }}>
      <div className="max-w-7xl mx-auto">
        <Button
          onClick={() => navigate('/age-selection')}
          variant="ghost"
          className="mb-6 text-lg font-semibold hover:scale-110 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="text-center mb-8 md:mb-12">
          <div className="text-6xl md:text-8xl mb-4 animate-bounce">🎈</div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-4" style={{
            fontFamily: '"Comic Neue", "Fredoka", cursive, sans-serif'
          }}>
            Hi {playerName}! What Do You Want to Learn?
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 font-bold">Pick a Fun Topic! 🌟</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {topics.map((topic, index) => {
            const IconComponent = topic.icon;
            const isHovered = hoveredTopic === topic.id;
            const isPlaying = playingSound === topic.id;
            return (
              <Card
                key={topic.id}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${isPlaying ? 'scale-105' : 'hover:scale-105'} hover:shadow-2xl`}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  borderRadius: '24px',
                  border: isHovered ? '4px solid #FFD700' : '3px solid transparent',
                  boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)'
                }}
                onClick={() => handleTopicClick(topic)}
                onMouseEnter={() => handleTopicHover(topic)}
                onMouseLeave={() => setHoveredTopic(null)}
              >
                {/* Image Section */}
                <div 
                  className="h-40 md:h-48 relative overflow-hidden"
                  style={{ backgroundColor: topic.bgColor }}
                >
                  <img 
                    src={topic.image} 
                    alt={topic.label}
                    className={`w-full h-full object-cover transition-transform duration-500 ${isHovered ? 'scale-110' : 'scale-100'}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/30 to-transparent`}></div>
                  
                  {/* Emoji Badge */}
                  <div className={`absolute top-3 right-3 text-4xl md:text-5xl ${isHovered || isPlaying ? 'animate-bounce' : ''}`}>
                    {topic.emoji}
                  </div>
                  
                  {/* Sound indicator when playing */}
                  {isPlaying && (
                    <div className="absolute top-3 left-3 bg-white/90 rounded-full p-2 animate-pulse">
                      <Volume2 className="w-5 h-5 text-red-500" />
                    </div>
                  )}
                  
                  {/* Icon Badge */}
                  <div className="absolute bottom-3 left-3 bg-white/90 rounded-full p-2 shadow-lg">
                    <IconComponent className="w-5 h-5 md:w-6 md:h-6" style={{ color: topic.bgColor }} />
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-4 md:p-5 bg-white">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-1 text-center" style={{
                    fontFamily: '"Fredoka", "Comic Neue", cursive, sans-serif'
                  }}>
                    {topic.label}
                  </h3>
                  <p className="text-sm md:text-base text-gray-600 text-center mb-3">{topic.description}</p>
                  
                  {/* Fun Fact - shows on hover */}
                  {isHovered && (
                    <p className="text-xs md:text-sm text-amber-600 text-center mb-3 font-medium animate-pulse">
                      💡 {topic.funFact}
                    </p>
                  )}
                  
                  <div
                    className={`w-full py-3 text-base md:text-lg font-bold text-center text-white transition-all duration-300 rounded-xl ${isPlaying ? 'animate-pulse' : ''}`}
                    style={{
                      background: `linear-gradient(135deg, ${topic.bgColor}, ${topic.bgColor}dd)`,
                      fontFamily: '"Fredoka", cursive'
                    }}
                  >
                    {isPlaying ? '🔊 Playing...' : "Let's Play! 🎮"}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Fun decorative elements */}
      </div>
    </div>
  );
};

export default PreschoolTopics;
